import React, {useState} from 'react';

import {FieldProp} from '../../types';
import {DisplayOnly} from './DisplayOnly';
import {Etl} from '../../_services/Etl';
import {Lambda} from '../../_services/Lambda';
import {applyFillMap} from '../../_helpers/etl';
import {ActivityButton} from '../ActivityButton';

type TextProps = FieldProp & {
    type?: string;
};

export const Text: React.FunctionComponent<TextProps> = ({
                                                             field,
                                                            formState,
                                                             fieldState,
                                                             type,
                                                             value,
                                                             displayOnly,
                                                             onChange,
                                                             onBlur,
                                                         }): JSX.Element => {

    const [etlIsWorking, setEtlIsWorking] = useState<boolean>(false);
    const [etlErrorMessage, setEtlErrorMessage] = useState<string>('');

    const {
        touched,
        pristine,
        validating,
        valid,
        invalid,
        errors,
    } = fieldState;

    const componentCss = field?.flowCustomDisplayComponentConfig?.component?.css || false;
    const componentStyles = field?.flowCustomDisplayComponentConfig?.component?.styles || false;

    const classNames = [
        'form-control',
        touched && 'is-touched',
        pristine && 'is-pristine',
        validating && 'is-validating',
        valid && 'is-valid',
        invalid && 'is-invalid',
        componentCss
    ]
        .filter(Boolean)
        .join(' ');

    const containerClassNames = [
        'input-group',
        'activity-group',
        etlIsWorking && 'is-working'
    ]
        .filter(Boolean)
        .join(' ');

    // ETL options
    const etlTrigger = (field?.flowCustomDisplayComponentConfig?.hasOwnProperty('etlTrigger')) ? field.flowCustomDisplayComponentConfig.etlTrigger : 'blur';
    const etlButton = {
        classNames: '',
        label: 'Lookup',
        ...(field?.flowCustomDisplayComponentConfig?.etlButton || {})
    };

    const hideCharacterCount = field?.flowCustomDisplayComponentConfig?.hideCharacterCount || false;

    // add logic to display ETL error below input

    const handleEtl = () => {
        setEtlErrorMessage('');
        if (
            value &&
            field.etlConfig instanceof Object &&
            Object.keys(field.etlConfig).length > 0
        ) {
            setEtlIsWorking(true);
            const resultProperty = field.etlConfig.resultProperty || 'result';
            const requestObject = {
                [field.etlConfig.requestValueProperty]: value,
                ...field.etlConfig.additionalRequestProperties,
                // tslint:disable-next-line:no-eval
                ...(field.etlConfig?.additionalRequestPropertiesCallback ? eval(field.etlConfig.additionalRequestPropertiesCallback) : {})
            };

            const executor = field.etlConfig?.useLambda
                ? Lambda.getInstance().execute.bind(Lambda.getInstance())
                : Etl.getInstance().process.bind(Etl.getInstance());

            executor(field.etlConfig.pipeline, requestObject)
                .then((response) => {
                    if (response.ok) {
                        let result: object | object[] = response.json[resultProperty];
                        if (result instanceof Array) {
                            result = result.length > 0 ? result[0] : {};
                        }
                        const stateUpdates = applyFillMap(field.etlConfig.fillMap, result);
                        console.log('apply state updates', stateUpdates);
                        onBlur && onBlur(stateUpdates);
                    } else {
                        throw Error(response?.json?.message);
                    }
                })
                .catch((error: any) => {
                    console.log('Error in ETL', error);
                    setEtlErrorMessage(error?.message || 'Error in request');
                })
                .finally(() => {
                    setEtlIsWorking(false);
                })
        }
    }

    return (
        <div>
            {displayOnly ? <DisplayOnly>{value}</DisplayOnly> :
                <div className={containerClassNames}>
                    <input
                        id={field.htmlId}
                        type={type || 'text'}
                        required={field.isRequired}
                        readOnly={field.gwreadonly_enable}
                        className={classNames}
                        placeholder={field.placeholder}
                        onBlur={() => {
                            etlTrigger === 'blur' && handleEtl();
                            onBlur && onBlur();
                        }}
                        onChange={(e) => {
                            onChange(e.target.value);
                        }}
                        value={value || ''}
                        style={componentStyles || {}}
                    />
                    {etlIsWorking && etlTrigger === 'blur' &&
                    <div className="input-group-append">
                            <span className="input-group-text">
                            <i className="fas fa-spinner fa-spin"></i>
                            </span>
                    </div>
                    }

                    {etlTrigger === 'button' &&
                    <ActivityButton type="button"
                                    isActive={etlIsWorking}
                                    className={`btn btn-secondary ${etlButton.classNames}`}
                                    onClick={handleEtl}
                    >{etlButton.label}</ActivityButton>
                    }
                </div>
            }

            {etlErrorMessage &&
                <div className="text-left">
                    <span className="small text-danger">{etlErrorMessage}</span>
                </div>
            }

            <div className="d-flex">
                <div className="flex-grow-1">
                    {errors && errors.map((error: string, index: number) => (
                        <div key={index} className="invalid-feedback">
                            {error}
                        </div>
                    ))}
                </div>

                {!displayOnly && field.maxLength && !hideCharacterCount &&
                <div className="text-right" style={{whiteSpace: 'nowrap'}}>
                    <span className="small text-muted">{value ? value.length : 0} of {field.maxLength}</span>
                </div>
                }
            </div>
        </div>
    );
};
